import {
    AccountInfo,
    AuthenticationResult,
    BrowserAuthError,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from '@azure/msal-browser';
import { loginRequest, silentRedirectUri } from 'app/auth/authConfig';
import { msalInstance, msalInitPromise } from 'app/auth/msalInstance';

export class NotAuthenticatedError extends Error {
    constructor(message = 'No active account. You must log in.') {
        super(message);
        this.name = 'NotAuthenticatedError';
    }
}

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const SILENT_TOKEN_RETRY_DELAY_MS = 500;

let inFlightTokenRequest: Promise<AuthenticationResult> | null = null;
let cachedToken: AuthenticationResult | null = null;

function resolveActiveAccount(instance: PublicClientApplication): AccountInfo {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (!account) {
        throw new NotAuthenticatedError();
    }
    if (!instance.getActiveAccount()) {
        instance.setActiveAccount(account);
    }
    return account;
}

function isTokenFresh(result: AuthenticationResult): boolean {
    if (!result.expiresOn) {
        return false;
    }
    return result.expiresOn.getTime() - Date.now() > TOKEN_REFRESH_BUFFER_MS;
}

function isRecoverableSilentAuthError(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }
    if (error instanceof BrowserAuthError) {
        return [
            'interaction_required',
            'consent_required',
            'login_required',
            'block_iframe_reload',
            'monitor_window_timeout',
            'timed_out',
            'time_out',
            'no_tokens_found',
        ].includes(error.errorCode);
    }
    return false;
}

function isTransientSilentAuthError(error: unknown): boolean {
    return error instanceof BrowserAuthError && [
        'monitor_window_timeout',
        'timed_out',
        'time_out',
    ].includes(error.errorCode);
}

async function acquireTokenSilentOnce(
    instance: PublicClientApplication,
    account: AccountInfo,
    forceRefresh: boolean,
): Promise<AuthenticationResult> {
    return instance.acquireTokenSilent({
        ...loginRequest,
        account,
        redirectUri: silentRedirectUri,
        forceRefresh,
    });
}

async function acquireTokenWithFallback(
    instance: PublicClientApplication,
    account: AccountInfo,
    forceRefresh: boolean,
): Promise<AuthenticationResult> {
    try {
        return await acquireTokenSilentOnce(instance, account, forceRefresh);
    } catch (error) {
        if (isTransientSilentAuthError(error)) {
            await new Promise(resolve => setTimeout(resolve, SILENT_TOKEN_RETRY_DELAY_MS));
            try {
                return await acquireTokenSilentOnce(instance, account, forceRefresh);
            } catch (retryError) {
                if (isRecoverableSilentAuthError(retryError)) {
                    await instance.acquireTokenRedirect({ ...loginRequest, account });
                }
                throw retryError;
            }
        }

        if (isRecoverableSilentAuthError(error)) {
            await instance.acquireTokenRedirect({ ...loginRequest, account });
        }
        throw error;
    }
}

export async function getAccessToken(
    instance: PublicClientApplication = msalInstance,
    forceRefresh = false,
): Promise<AuthenticationResult> {
    await msalInitPromise;

    const account = resolveActiveAccount(instance);

    if (!forceRefresh && cachedToken && isTokenFresh(cachedToken)) {
        return cachedToken;
    }

    if (inFlightTokenRequest && !forceRefresh) {
        return inFlightTokenRequest;
    }

    const tokenPromise = acquireTokenWithFallback(instance, account, forceRefresh)
        .then(result => {
            cachedToken = result;
            return result;
        })
        .finally(() => {
            if (inFlightTokenRequest === tokenPromise) {
                inFlightTokenRequest = null;
            }
        });

    inFlightTokenRequest = tokenPromise;
    return tokenPromise;
}

export function clearAccessTokenCache(): void {
    cachedToken = null;
    inFlightTokenRequest = null;
}
